/**
 * Sidebar navigation component
 */
import {
    CompareArrows,
    ChevronLeft,
    ChevronRight,
    Description,
    Home,
    History,
    Map as MapIcon,
    PlayArrow,
} from '@mui/icons-material';
import {
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Typography
} from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const sidebarItems: SidebarItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: <Home />,
    path: '/app/home',
  },
  {
    id: 'map',
    label: 'Map',
    icon: <MapIcon />,
    path: '/app/map',
  },
  {
    id: 'history',
    label: 'History',
    icon: <History />,
    path: '/app/history',
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: <Description />,
    path: '/app/templates',
  },
  {
    id: 'scenarios',
    label: 'Scenarios',
    icon: <PlayArrow />,
    path: '/app/scenarios',
  },
  {
    id: 'scenario-comparison',
    label: 'Compare Scenarios',
    icon: <CompareArrows />,
    path: '/app/scenario-comparison',
  },
];

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  open = true,
  onClose,
  variant = 'permanent',
  collapsed = false,
  onToggleCollapse
}) => {

  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{
      width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
      height: 'calc(100vh - 64px)', // Full height minus AppBar
      pb: 8, // Add bottom padding to prevent content from being hidden behind the button
    }}>
      {/* Sidebar Header */}
      <Box sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 56,
      }}>
        <Typography variant="h6" color="primary" fontWeight="bold">
          Navigation
        </Typography>
      </Box>

      {/* Navigation Items */}
      <List sx={{ pt: 2 }}>
        {sidebarItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 'auto' : 40,
                    mr: collapsed ? 0 : undefined,
                    justifyContent: 'center',
                    color: location.pathname === item.path ? 'inherit' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* Collapse button at bottom */}
      {variant === 'permanent' && onToggleCollapse && (
        <Box sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          width: collapsed ? SIDEBAR_COLLAPSED_WIDTH - 32 : SIDEBAR_WIDTH - 32,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              color: 'primary.main',
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.dark',
              },
            }}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        '& .MuiDrawer-paper': {
          width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRight: 1,
          borderColor: 'divider',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH };
